import type { IconName } from './MaterialIcon.js';

import * as React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { IconSizes, MaterialIcon } from './MaterialIcon.js';

export interface SegmentedControlOption {
  value: string;
  label: string;
  icon?: IconName;
}

export interface SegmentedControlProps {
  options: SegmentedControlOption[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  ariaLabel?: string;
  /** When set, the icon for this option value plays a pulse animation */
  animatingValue?: string | null;
  /** Override the animation class per option (defaults to 'animate-nav-pulse') */
  animatingClassName?: (value: string) => string;
}

const SegmentedControl = React.forwardRef<HTMLDivElement, SegmentedControlProps>(
  ({ options, value, onValueChange, className, ariaLabel, animatingValue, animatingClassName }, ref) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [indicatorStyle, setIndicatorStyle] = React.useState<React.CSSProperties>({});
    const groupName = React.useId();

    // Update indicator position when value changes
    React.useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const activeButton = container.querySelector(`input[value="${value}"]`)?.closest('label') as HTMLElement;
      if (!activeButton) return;

      setIndicatorStyle({
        left: activeButton.offsetLeft,
        top: activeButton.offsetTop,
        width: activeButton.offsetWidth,
        height: activeButton.offsetHeight,
      });
    }, [value, options]);

    return (
      <div ref={ref} className={cn('relative inline-flex items-center text-sm', className)}>
        {/* Radio button group for accessibility */}
        <div
          ref={containerRef}
          role="radiogroup"
          className="relative flex gap-1 items-center w-full"
          aria-label={ariaLabel}
        >
          {/* Animated background indicator */}
          <div className="absolute rounded-sm transition-all duration-200 ease-out bg-primary" style={indicatorStyle} />

          {options.map((option) => (
            <label
              key={option.value}
              className={cn(
                'relative cursor-pointer rounded-sm transition-text duration-200 font-medium px-3 py-2',
                'flex items-center justify-center',
                'has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
                value === option.value
                  ? 'text-primary-foreground'
                  : 'text-foreground hover:bg-secondary hover:text-secondary-foreground',
              )}
            >
              <input
                type="radio"
                name={groupName}
                value={option.value}
                checked={value === option.value}
                onChange={() => onValueChange(option.value)}
                className="sr-only"
                aria-label={option.label}
              />
              <div className="flex items-center gap-2 flex-row">
                {option.icon && (
                  <MaterialIcon
                    icon={option.icon}
                    size={IconSizes.sm}
                    className={cn(
                      'flex-shrink-0',
                      animatingValue === option.value && (animatingClassName?.(option.value) ?? 'animate-nav-pulse'),
                    )}
                    filled={animatingValue === option.value}
                  />
                )}
                <span className="text-sm">{option.label}</span>
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  },
);

SegmentedControl.displayName = 'SegmentedControl';

export { SegmentedControl };
